import { useEffect, useRef, useState } from "react";
import { CheckCircle, RefreshCw } from "lucide-react";
import { initSCORM, teardownSCORM } from "../../../core/services/scorm-api";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import ToastService from "../../../core/services/toaster-service";

/**
 * CourseViewer
 *
 * IMPORTANT: initSCORM() is called synchronously during the render phase,
 * BEFORE the iframe is inserted into the DOM. This is required because
 * Articulate 360 looks for window.API / window.API_1484_11 the moment the
 * course content starts loading — if the API is not already on window, the
 * course throws "Content launched outside of a supported LMS environment".
 */
export default function CourseViewer({
  courseId,
  courseUrl,
  courseTitle = "Course",
  initialProgress = 0,
}) {
  const [progress, setProgress] = useState(initialProgress);
  const [status, setStatus] = useState("not attempted");
  const [saving, setSaving] = useState(false);

  // Keep progress bar in sync when parent re-fetches initial data
  useEffect(() => {
    setProgress(initialProgress);
  }, [initialProgress]);

  // Stable callback ref — avoids stale-closure issues while keeping the
  // function identity stable so the synchronous init below only runs once.
  const onCommitRef = useRef(null);
  onCommitRef.current = async (payload) => {
    setProgress(payload.coursePercentage);
    setStatus(payload.status);
    setSaving(true);
    try {
      await axiosService.post(ApiRoutes.UPDATE_COURSE_PROGRESS, payload);
    } catch {
      ToastService.error("Failed to save progress. Please check your connection.");
    } finally {
      setSaving(false);
    }
  };

  // ── Synchronous init — runs during render, BEFORE the iframe mounts ──────
  // A ref guards against double-initialisation in Strict Mode / fast refresh.
  const scormInitialized = useRef(false);
  if (!scormInitialized.current) {
    initSCORM({
      courseId,
      onCommit: (payload) => onCommitRef.current(payload),
    });
    scormInitialized.current = true;
  }

  // Teardown on unmount only
  useEffect(() => {
    return () => {
      teardownSCORM();
      scormInitialized.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isCompleted =
    status === "completed" || status === "passed" || progress >= 100;

  return (
    <div className="course-viewer">
      {/* ── Slim status bar ── */}
      <div className="course-viewer__status-bar">
        <div className="course-viewer__progress-track">
          <div
            className="course-viewer__progress-fill"
            style={{ "--progress-width": `${Math.min(100, progress)}%` }}
          />
        </div>

        <div className="course-viewer__meta">
          {saving ? (
            <span className="course-viewer__saving">
              <RefreshCw size={12} className="course-viewer__saving-icon" />
              Saving…
            </span>
          ) : isCompleted ? (
            <span className="course-viewer__completed">
              <CheckCircle size={14} />
              Completed
            </span>
          ) : (
            <span className="course-viewer__percent">{progress}% complete</span>
          )}
        </div>
      </div>

      {/* ── Course iframe or placeholder ── */}
      {courseUrl ? (
        <iframe
          src={courseUrl}
          title={courseTitle}
          className="course-viewer__frame"
          allowFullScreen
          allow="fullscreen"
        />
      ) : (
        <div className="course-viewer__placeholder">
          <p>Course URL is not configured yet.</p>
        </div>
      )}
    </div>
  );
}
